import { Lightning } from '@lightningjs/sdk'

import { getColor, grey50, transparent } from '../../constants/colors'
import { UniNeueBold } from '../../constants/fonts'
import { LEFT_SAFE_ZONE_MARGIN } from '../../constants/general'
import { AppConfig } from '../../services/appConfig'
import { CardItemModel, TvCarouselContainerModel } from '../../types/flo-experience'
import { toCard, toCardRef } from '../../utils/carouselUtils'
import { NavigationPointers, PointerOrientation } from '../NavigationPointers/NavigationPointers'
import { FOCUS_BORDER_WIDTH } from '../TvCards/TvBaseCard'

const CAROUSEL_TITLE_HEIGHT = 58
const CAROUSEL_TITLE_MARGIN_BOTTOM = 32

export default class TvCarouselContainer extends Lightning.Component {
  private _items: CardItemModel[] = []
  private _carouselIndex = 0
  private _currentCardIndex = 0
  private _totalCards = 0
  private _cardWidth = 0
  private _carouselType: string
  private _cardHeight = 0
  private _cardStyle: string
  private _titleSize = CAROUSEL_TITLE_HEIGHT + CAROUSEL_TITLE_MARGIN_BOTTOM
  private _currentOffset = 0
  private _data: TvCarouselContainerModel
  private _viewportWidth = window.screen.width ?? window.innerWidth

  static _template() {
    return {
      w: 1920,
      h: (h) => h,
      rect: true,
      color: transparent,
      collision: true,
      flex: { direction: 'column' },
      Title: {
        h: CAROUSEL_TITLE_HEIGHT,
        flexItem: {
          marginBottom: CAROUSEL_TITLE_MARGIN_BOTTOM,
        },
        text: {
          text: '',
          fontSize: 48,
          fontFace: UniNeueBold,
          color: getColor(grey50),
        },
      },
      Wrapper: {
        w: (w) => w,
        h: (h) => h,

        ScrollContainer: {
          flex: { direction: 'row' },
        },
        NavigationPointers: {
          type: NavigationPointers,
          pointerOrientation: PointerOrientation.HORIZONTAL,
          h: (h) => h,
        },
      },
    }
  }

  get sectionText(): string {
    return this.title.text.text + ' . ' + this.itemString + ', Scroll to Browse'
  }

  get itemString(): string {
    if (this._totalCards > 1) return this._totalCards + ' Items'
    return this._totalCards + ' Item'
  }

  get scrollContainer() {
    return this.tag('ScrollContainer')
  }

  // TODO: This setter has a different type then the getter below
  set title(v) {
    this.title.patch({ text: v })

    // Refresh title visibility if needed
    if (this._cardHeight > 0) {
      this.cardHeight = this._cardHeight
    }
  }

  get title() {
    return this.tag('Title')
  }

  get navigationPointers() {
    return this.tag('NavigationPointers') as NavigationPointers
  }

  get hasTitle() {
    return this.title.text.text
  }

  set items(v: CardItemModel[]) {
    this._items = v
    this._totalCards = this._items.length
  }

  set carouselIndex(v) {
    this._carouselIndex = v
  }

  get carouselIndex() {
    return this._carouselIndex
  }

  set carouselType(v) {
    this._carouselType = v
  }

  set cardWidth(width: number) {
    this._cardWidth = width
  }

  set cardHeight(height: number) {
    this._cardHeight = height

    this.patch({
      h: (this.hasTitle ? this._titleSize : 0) + this._cardHeight,
      Title: {
        visible: this.hasTitle,
      },
    })
  }

  set cardStyle(v) {
    this._cardStyle = v
  }

  get isFirstCardFocused() {
    return this._currentCardIndex === 0
  }

  get isLastCardFocused() {
    return this._currentCardIndex === this._totalCards - 1
  }

  get currentCard() {
    return this.scrollContainer.childList.getByRef(toCardRef(this._currentCardIndex))
  }

  get currentOffset() {
    return this._currentOffset
  }

  set data(v: TvCarouselContainerModel) {
    this._data = v
  }

  get data() {
    return this._data
  }

  get currentCardIndex() {
    return this._currentCardIndex
  }

  set currentCardIndex(index: number) {
    this._currentCardIndex = index
  }

  set currentOffset(v: number) {
    this.scrollContainer.x = v
  }

  get items() {
    return this._items
  }

  get maxScroll() {
    return this.scrollContainer.finalW - this._viewportWidth + LEFT_SAFE_ZONE_MARGIN
  }

  _init() {
    this.renderCards()
    this._refocus()

    // Even using this.stage.update() doesn't update the latest values for the finalH of the scrollContainer nor the maxScroll
    // on the initial component render so we have to rely on requestAnimationFrame to get the latest values
    requestAnimationFrame(() => this.checkNavigationPointersVisibility())
  }

  _getFocused() {
    return this.currentCard
  }

  _handleLeft() {
    return this.handleScroll({ deltaX: -1 })
  }

  _handleRight() {
    this.handleScroll({ deltaX: 1 })
  }

  renderCards() {
    const cards = this._items?.map((item, index) =>
      toCard(
        item,
        index,
        this._carouselIndex,
        this._cardHeight,
        this._cardWidth,
        this._cardStyle,
        this._items?.length,
      ),
    )

    this.scrollContainer.patch({ children: cards })
  }

  handleScroll({ deltaX }) {
    const isGoingRight = deltaX > 0

    if ((!isGoingRight && this.isFirstCardFocused) || (isGoingRight && this.isLastCardFocused)) {
      return false
    }

    this._currentCardIndex += isGoingRight ? 1 : -1

    this._currentOffset = -this.currentCard.finalX

    if (this.scrollContainer.finalW + LEFT_SAFE_ZONE_MARGIN > this._viewportWidth) {
      this.scroll(Math.max(this._currentOffset, -this.maxScroll))
    }

    return true
  }

  scroll(v: number) {
    this.scrollContainer.setSmooth('x', v, {
      duration: 0.2,
    })
  }

  updateCards(items: CardItemModel[]) {
    const cards = items?.map((item, index) =>
      toCard(
        item,
        index,
        this._carouselIndex,
        this._cardHeight,
        this._cardWidth,
        this._cardStyle,
        items.length,
      ),
    )

    this._currentCardIndex = 0
    this._totalCards = items.length
    this.scroll(0)

    this.scrollContainer.childList.clear()
    this.scrollContainer.patch({ children: cards })
    this._refocus()
  }

  // Use only to restore the focus when
  // returning back to a carousel page
  setXAxis(v: number) {
    // Make sure final coordinates are correct before calculating
    // https://lightningjs.io/docs/#/lightning-core-reference/Templates/Flexbox?id=final-coordinates
    this.stage.update()
    this.scrollContainer.x = v
    this._currentOffset = v
  }

  $leftMovement() {
    this._handleLeft()
    this.checkNavigationPointersVisibility()
  }

  $rightMovement() {
    this._handleRight()
    this.checkNavigationPointersVisibility()
  }

  $nextPageLoadComplete() {
    this.checkNavigationPointersVisibility()
  }

  checkNavigationPointersVisibility() {
    const { isCursorVisible } = AppConfig.shared.appSettings
    const showRightPointer =
      Math.abs(this._currentOffset) < this.maxScroll &&
      this.scrollContainer.finalW > this._viewportWidth + LEFT_SAFE_ZONE_MARGIN
    const showLeftPointer = !this.isFirstCardFocused && this._currentOffset !== 0

    this.navigationPointers.h = this.scrollContainer.finalH + FOCUS_BORDER_WIDTH * 2
    this.navigationPointers.y = -FOCUS_BORDER_WIDTH
    this.navigationPointers.renderGradient()

    this.navigationPointers.showLeftPointer = showLeftPointer && isCursorVisible
    this.navigationPointers.showRightPointer = showRightPointer && isCursorVisible

    this._refocus()
  }

  $updateFocusByHover({ cardIndex, buttonIndex }) {
    this._currentCardIndex = Number.isInteger(cardIndex) ? cardIndex : buttonIndex
    this._refocus()

    // Forwards the hover event to the CarouselHandler
    this.fireAncestors('$updateFocusByHover', this)
  }
}
